import { useCallback, useMemo, useState } from "react";
import {
  Copy,
  Check,
  ArrowSquareOut,
  Link as LinkIcon,
  ArrowsClockwise,
  PencilSimple,
  FloppyDisk,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { updateAdFields, updateVariantText, variantTrackUrl, regenerateVariant, autoFixVariant } from "../lib/api";
import { COPY_INDICATOR_MS, PLATFORM_META } from "../lib/constants";
import VariantSwitcher from "./VariantSwitcher";
import CreativeSwitcher from "./CreativeSwitcher";
import ComplianceBadge from "./ComplianceBadge";
import EditableField from "./EditableField";

/**
 * Build the full copy string for an active variant (headline + primary text
 * + description + hashtags + tracker URL).
 */
function buildFullText(variant, ad, url, adSetId) {
  const hashtags = ad.hashtags?.length
    ? ad.hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")
    : "";
  const destUrl = adSetId
    ? variantTrackUrl(adSetId, ad.platform, variant.id)
    : url;
  return [variant.headline, variant.primary_text, ad.description, hashtags, destUrl]
    .filter(Boolean)
    .join("\n\n");
}

function useCopyIndicator() {
  const [copied, setCopied] = useState(false);
  const trigger = useCallback(async (text, successMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(successMsg);
      setTimeout(() => setCopied(false), COPY_INDICATOR_MS);
    } catch {
      toast.error("Copy failed");
    }
  }, []);
  return [copied, trigger];
}

export default function AdCard({
  ad,
  creatives = [],
  url,
  adSetId,
  performance,
  complianceResults,
  tone = "energetic",
  audience = "",
  onAdSetUpdate,
}) {
  const meta = PLATFORM_META[ad.platform] || {
    label: ad.platform,
    color: "#ccff00",
  };
  const variants = ad.variants?.length
    ? ad.variants
    : [{ id: "primary", headline: ad.headline, primary_text: ad.primary_text }];

  const [activeIdx, setActiveIdx] = useState(0);
  const [aspect, setAspect] = useState("1:1");
  const [regenerating, setRegenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [draftHeadline, setDraftHeadline] = useState("");
  const [draftBody, setDraftBody] = useState("");
  // Platform-level inline edits (description / cta / hashtags). Only one field
  // may be actively edited at a time. `editingField` holds the field name or
  // null; `draftField` holds the current text/list draft.
  const [editingField, setEditingField] = useState(null);
  const [draftField, setDraftField] = useState("");
  const [savingField, setSavingField] = useState(false);
  const [copiedText, doCopyText] = useCopyIndicator();
  const [copiedLink, doCopyLink] = useCopyIndicator();

  const perfMap = useMemo(
    () => new Map((performance?.variants || []).map((v) => [v.variant_id, v])),
    [performance],
  );
  const winnerId = performance?.winner_variant_id;
  const activeVariant = variants[activeIdx];
  const activePerf = perfMap.get(activeVariant.id);
  const activeCreative =
    creatives.find((c) => c.aspect === aspect) || creatives[0] || null;
  const activeCompliance = complianceResults?.find(
    (r) => r.variant_id === activeVariant.id,
  );

  const handleCopyText = useCallback(() => {
    const text = buildFullText(activeVariant, ad, url, adSetId);
    doCopyText(
      text,
      `${meta.label} · variant ${String.fromCharCode(65 + activeIdx)} copied`,
    );
  }, [activeVariant, ad, url, adSetId, doCopyText, meta.label, activeIdx]);

  const handleCopyTracker = useCallback(() => {
    if (!adSetId) return toast.error("Tracker link unavailable");
    const trackerUrl = variantTrackUrl(adSetId, ad.platform, activeVariant.id);
    doCopyLink(trackerUrl, "Tracker URL copied (with UTM)");
  }, [adSetId, ad.platform, activeVariant.id, doCopyLink]);

  const handleDownloadImg = useCallback(() => {
    if (!activeCreative?.data) return;
    const a = document.createElement("a");
    a.href = `data:${activeCreative.mime_type};base64,${activeCreative.data}`;
    a.download = `adforge-${ad.platform}-${aspect.replace(":", "x")}.png`;
    a.click();
  }, [activeCreative, ad.platform, aspect]);

  const handleRegenerate = useCallback(
    async (aroundWinner = false) => {
      if (!adSetId) return;
      setRegenerating(true);
      try {
        const updated = await regenerateVariant(adSetId, {
          platform: ad.platform,
          variant_index: activeIdx,
          around_winner: aroundWinner,
          tone,
          audience,
        });
        toast.success(
          `Variant ${String.fromCharCode(65 + activeIdx)} regenerated${aroundWinner ? " around winner" : ""}`,
        );
        onAdSetUpdate?.(updated);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Regenerate failed");
      } finally {
        setRegenerating(false);
      }
    },
    [adSetId, ad.platform, activeIdx, tone, audience, onAdSetUpdate],
  );

  const startEdit = useCallback(() => {
    setDraftHeadline(activeVariant.headline || "");
    setDraftBody(activeVariant.primary_text || "");
    setEditing(true);
  }, [activeVariant]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraftHeadline("");
    setDraftBody("");
  }, []);

  const saveEdit = useCallback(async () => {
    if (!adSetId) return;
    if (!draftHeadline.trim() && !draftBody.trim()) {
      toast.error("Headline or body must not be empty");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateVariantText(adSetId, {
        platform: ad.platform,
        variant_id: activeVariant.id,
        headline: draftHeadline,
        primary_text: draftBody,
      });
      toast.success(`Variant ${String.fromCharCode(65 + activeIdx)} updated`);
      onAdSetUpdate?.(updated);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [adSetId, ad.platform, activeVariant.id, activeIdx, draftHeadline, draftBody, onAdSetUpdate]);

  const handleAutoFix = useCallback(async () => {
    if (!adSetId) return;
    setAutoFixing(true);
    try {
      const updated = await autoFixVariant(adSetId, {
        platform: ad.platform,
        variant_id: activeVariant.id,
      });
      toast.success(
        `Variant ${String.fromCharCode(65 + activeIdx)} auto-fixed · re-run compliance check to verify`,
      );
      onAdSetUpdate?.(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-fix failed");
    } finally {
      setAutoFixing(false);
    }
  }, [adSetId, ad.platform, activeVariant.id, activeIdx, onAdSetUpdate]);

  // Click a field label to start editing that platform-level field. `hashtags`
  // is edited as a comma-separated string for simplicity.
  const startFieldEdit = useCallback((field) => {
    const current = field === "hashtags"
      ? (ad.hashtags || []).map((h) => h.replace(/^#/, "")).join(", ")
      : (ad[field] || "");
    setDraftField(current);
    setEditingField(field);
  }, [ad]);

  const cancelFieldEdit = useCallback(() => {
    setEditingField(null);
    setDraftField("");
  }, []);

  const saveFieldEdit = useCallback(async () => {
    if (!adSetId || !editingField) return;
    setSavingField(true);
    try {
      const payload = { platform: ad.platform };
      if (editingField === "hashtags") {
        payload.hashtags = draftField
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
      } else {
        payload[editingField] = draftField;
      }
      const updated = await updateAdFields(adSetId, payload);
      toast.success(`${editingField} updated`);
      onAdSetUpdate?.(updated);
      setEditingField(null);
      setDraftField("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingField(false);
    }
  }, [adSetId, editingField, draftField, ad.platform, onAdSetUpdate]);

  return (
    <div
      className="bg-[#0a0a0a] border border-white/10 hover:border-white/20 transition-colors duration-300 p-6 flex flex-col fade-up"
      data-testid={`ad-card-${ad.platform}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-6" style={{ backgroundColor: meta.color }} />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">
            {meta.label}
          </span>
        </div>
        <div className="flex gap-1">
          <button
            data-testid={`copy-ad-${ad.platform}`}
            onClick={handleCopyText}
            className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
            title="Copy ad copy + tracker URL"
          >
            {copiedText ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <button
            data-testid={`copy-tracker-${ad.platform}`}
            onClick={handleCopyTracker}
            className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
            title="Copy tracker URL only (with UTM)"
          >
            {copiedLink ? <Check size={16} /> : <LinkIcon size={16} />}
          </button>
          {activeCreative?.data && (
            <button
              data-testid={`download-img-${ad.platform}`}
              onClick={handleDownloadImg}
              className="p-2 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
              title={`Download ${aspect} creative`}
            >
              <ArrowSquareOut size={16} />
            </button>
          )}
        </div>
      </div>

      <CreativeSwitcher
        creatives={creatives}
        aspect={aspect}
        onSelect={setAspect}
        platform={ad.platform}
      />

      <VariantSwitcher
        variants={variants}
        activeIdx={activeIdx}
        onSelect={setActiveIdx}
        perfMap={perfMap}
        winnerId={winnerId}
        platform={ad.platform}
        activePerf={activePerf}
      />

      <div className="space-y-3 flex-1">
        {editing ? (
          <>
            <div>
              <div className="label-mono mb-1 flex items-center justify-between">
                <span>headline · editing</span>
                <span className="text-[10px] text-zinc-500">{draftHeadline.length} chars</span>
              </div>
              <input
                data-testid={`edit-headline-${ad.platform}`}
                value={draftHeadline}
                onChange={(e) => setDraftHeadline(e.target.value)}
                className="w-full bg-[#121212] border border-[#ccff00]/40 focus:border-[#ccff00] outline-none p-2 font-display text-lg font-bold text-white"
              />
            </div>
            <div>
              <div className="label-mono mb-1 flex items-center justify-between">
                <span>primary · editing</span>
                <span className="text-[10px] text-zinc-500">{draftBody.length} chars</span>
              </div>
              <textarea
                data-testid={`edit-body-${ad.platform}`}
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                rows={5}
                className="w-full bg-[#121212] border border-[#ccff00]/40 focus:border-[#ccff00] outline-none p-2 text-sm text-zinc-200 leading-relaxed resize-y"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={saveEdit}
                disabled={saving}
                data-testid={`edit-save-${ad.platform}`}
                className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] px-4 py-2 flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-50"
              >
                <FloppyDisk size={14} weight="bold" />
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                data-testid={`edit-cancel-${ad.platform}`}
                className="px-4 py-2 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white text-sm flex items-center gap-2"
              >
                <X size={14} />
                Cancel
              </button>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">
              Tip: save then re-run the compliance check to confirm the fix.
            </div>
          </>
        ) : (
          <>
            <div>
              <div className="label-mono mb-1 flex items-center justify-between">
                <span>headline</span>
                <button
                  onClick={startEdit}
                  data-testid={`edit-btn-${ad.platform}`}
                  className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[#ccff00]"
                  title="Edit this variant's copy manually"
                >
                  <PencilSimple size={11} />
                  edit
                </button>
              </div>
              <div
                key={`h-${activeVariant.id}`}
                className="font-display text-lg font-bold leading-tight text-white fade-up"
                data-testid={`variant-headline-${ad.platform}`}
              >
                {activeVariant.headline}
              </div>
            </div>
            <div>
              <div className="label-mono mb-1">primary</div>
              <div
                key={`p-${activeVariant.id}`}
                className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap fade-up"
              >
                {activeVariant.primary_text}
              </div>
            </div>
          </>
        )}
        {/* Platform-level fields: description, cta, hashtags. Click the
            label (or empty state) to edit. */}
        <EditableField
          label="description"
          fieldKey="description"
          value={ad.description}
          isEditing={editingField === "description"}
          draft={draftField}
          setDraft={setDraftField}
          saving={savingField}
          onStart={() => startFieldEdit("description")}
          onCancel={cancelFieldEdit}
          onSave={saveFieldEdit}
          multiline
          placeholder="Add a description…"
          platform={ad.platform}
        />
        <EditableField
          label="cta"
          fieldKey="cta"
          value={ad.cta}
          isEditing={editingField === "cta"}
          draft={draftField}
          setDraft={setDraftField}
          saving={savingField}
          onStart={() => startFieldEdit("cta")}
          onCancel={cancelFieldEdit}
          onSave={saveFieldEdit}
          maxLength={40}
          placeholder="Add a call-to-action…"
          platform={ad.platform}
        />
        <EditableField
          label="hashtags"
          fieldKey="hashtags"
          value={(ad.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
          isEditing={editingField === "hashtags"}
          draft={draftField}
          setDraft={setDraftField}
          saving={savingField}
          onStart={() => startFieldEdit("hashtags")}
          onCancel={cancelFieldEdit}
          onSave={saveFieldEdit}
          hint="Comma-separated · # prefix optional"
          placeholder="fitness, wellness, motivation"
          platform={ad.platform}
        />

        {activeCompliance && (
          <div className="pt-3">
            <div className="label-mono mb-2">compliance</div>
            <ComplianceBadge
              result={activeCompliance}
              onAutoFix={handleAutoFix}
              autoFixing={autoFixing}
            />
          </div>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
        <button
          data-testid={`regenerate-${ad.platform}`}
          onClick={() => handleRegenerate(false)}
          disabled={regenerating}
          className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:text-[#ccff00] transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise
            size={13}
            className={regenerating ? "animate-spin" : ""}
          />
          {regenerating
            ? "regenerating…"
            : `regenerate variant ${String.fromCharCode(65 + activeIdx)}`}
        </button>
        <div className="bg-[#ccff00] text-black px-3 py-1.5 font-mono text-xs font-bold tracking-wide">
          {ad.cta}
        </div>
      </div>
    </div>
  );
}
